<template>
  <dara-single-content-layout container-size="md">
    <template v-if="article" #content>
      <div v-if="pageReady">
        <dara-row class="d-flex align-items-center">
          <dara-col :xs="24" :span="20">
            <dara-author :article="article" />
          </dara-col>
          <dara-col :xs="24" :span="4" justify="right">
            <div class="d--flex justify-content--space-between align-items--center">
              <dara-button v-if="canEdit">
                <dara-article-link
                  :id="article.id"
                  action="edit">
                  {{ $t('edit') }}
                </dara-article-link>
              </dara-button>
              <dara-button v-if="canEdit" class="color--danger" @click="setDeleteArticle(article)">
                {{ $t('delete') }}
              </dara-button>
            </div>
          </dara-col>
        </dara-row>
      </div>

      <dara-modal-delete
        v-if="showModal(article)"
        :content="$t('are-you-sure-you-want-to-delete-this-article')"
        @close="setDeleteArticle(null)"
        @confirm="onDeleteArticle(article)">
      </dara-modal-delete>

      <div v-if="articleReady">
        <dara-row>
          <dara-col>
            <dara-editor :article="article" mode="view" :ready="onReady" />
          </dara-col>
        </dara-row>

        <div v-if="pageReady">
          <dara-row class="d-flex align-items-center mb-5">
            <dara-col>
              <dara-header size="sm">{{ $t('rate-this-article') }}</dara-header>
              <dara-article-rating :article="article" />
            </dara-col>
          </dara-row>
        </div>

        <dara-row v-if="relatedArticle && !track" class="mb-5">
          <dara-col>
            <dara-header size="md">{{ $t('read-more') }}</dara-header>
            <div v-if="relatedArticle">
              <dara-article-card :article="relatedArticle" />
            </div>
          </dara-col>
        </dara-row>
        <dara-row v-if="!!track" class="mb-5">
          <dara-col v-if="nextTrackArticle">
            <dara-header size="md">{{ $t('next-track-article') }}</dara-header>
            <dara-article-card :article="nextTrackArticle" :track="track" />
          </dara-col>
          <dara-col v-else>
            <dara-header class="text-muted" size="md">{{ $t('track-end') }}</dara-header>
          </dara-col>
        </dara-row>
      </div>
      <div v-else>
        <el-skeleton :rows="3" animated />
        <br />
        <br />
        <el-skeleton :rows="4" animated />
        <br />
        <br />
        <el-skeleton :rows="10" animated />
      </div>
    </template>
  </dara-single-content-layout>
</template>

<script>
import { updateArticleVisits, getArticleById, fetchRelatedArticle, removeArticle, fetchNextTrackArticle, markArticleAsReaded } from '@/modules/article/article.service';
import { goToArticleNotFound, goToDeniedArticle } from '@/modules/article/article.routes';
import { ARTICLE_ERRORS } from '@/modules/article/article.constants';
import { getters } from '@/modules/user/user.store';
import { USERS_ROLES } from '@/modules/user/user.constants';
import { toastError } from '@/services/toastService';
import { translate } from '@/services/translate.service';
import { getMinutes } from '@/services/timeService';
import { goToBasePage } from '@/router/route.service';

import DaraEditor from '@/components/editor/Editor.vue';
import DaraAuthor from '@/modules/article/components/ArticleAuthor.vue';
import DaraArticleRating from '@/modules/article/components/ArticleRating.vue';
import DaraArticleCard from '@/modules/article/components/ArticleCard.vue';
import DaraSingleContentLayout from '@/layouts/SingleContentLayout.vue';

export default {
  name: 'ArticlePage',
  components: {
    DaraAuthor,
    DaraEditor,
    DaraArticleCard,
    DaraSingleContentLayout,
    DaraArticleRating,
  },
  data() {
    return {
      loadingEditor: true,
      loadingArticle: true,
      article: null,
      nextTrackArticle: null,
      articleDelete: null,
      relatedArticle: null,
      user: getters.getUser(),

      start: new Date(),
      end: new Date(),
    };
  },
  computed: {
    id() {
      return this.$route.params?.id;
    },
    track() {
      return this.$route.params?.track;
    },
    articleReady() {
      return !this.loadingArticle;
    },
    pageReady() {
      return !this.loadingEditor && !this.loadingArticle;
    },
    canEdit() {
      return (this.user.permission > USERS_ROLES.WRITER || (this.article.user_id === this.user.id && this.user.permission > USERS_ROLES.READER));
    },
  },
  beforeDestroy() {
    this.markArticleAsReaded();
  },
  mounted() {
    this.fetchArticle();

    window.addEventListener('beforeunload', () => {
      this.markArticleAsReaded();
    });
  },
  methods: {
    markArticleAsReaded() {
      this.end = new Date();
      const readedTime = getMinutes(this.end.getTime() - this.start.getTime());
      if (readedTime > this.article.reading_time) {
        markArticleAsReaded(this.article);
      }
    },
    fetchArticle() {
      this.loadingArticle = true;
      getArticleById(this.id)
        .then(data => {
          this.article = data.data;
          updateArticleVisits({ ...this.article });
          if (this.track) {
            this.fetchNextTrackArticle();
          } else {
            this.fetchRelatedArticle();
          }
        })
        .catch(err => {
          if (err.response.data.code === '22P02' || (err.response.data.errors === ARTICLE_ERRORS[err.response.status] && err.response.status === 404)) {
            goToArticleNotFound(this.$router);
          } else if (err.response.data.errors === ARTICLE_ERRORS[err.response.status] && err.response.status === 403) {
            goToDeniedArticle(this.$router);
          }
          this.article = null;
        })
        .finally(() => {
          this.loadingArticle = false;
        });
    },
    fetchNextTrackArticle() {
      fetchNextTrackArticle(this.track, { article_id: this.id })
        .then(({ data }) => {
          this.nextTrackArticle = data;
        })
        .catch(() => {
          toastError(translate('it-was-not-possible-to-get-next-article'));
        });
    },
    fetchRelatedArticle() {
      fetchRelatedArticle(this.article.department_id, { currentArticle: this.article.id })
        .then(({ data }) => {
          this.relatedArticle = data;
        })
        .catch(() => {
          toastError(translate('it-was-not-possible-to-get-related-article'));
        });
    },
    onReady() {
      setTimeout(() => {
        this.loadingEditor = false;
      }, 500);
    },
    setDeleteArticle(article) {
      this.articleDelete = article;
    },
    showModal(article) {
      return this.articleDelete && this.articleDelete.id === article.id;
    },
    onDeleteArticle(article) {
      removeArticle(article).then(() => goToBasePage());
    },
  },
};
</script>
